import React, { useMemo } from 'react';
import { Form, Select, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useClientCustomersQuery } from '@/api/auth/queries';
import { useEffectiveClientId } from '@/contexts/AdminClientsContext';
import { formatCustomerDisplayName } from '@/components/appointments/appointmentClientDisplay';

interface RegisteredCustomerSelectorProps {
  appointmentType: 'user' | 'anonymous';
}

export const RegisteredCustomerSelector: React.FC<RegisteredCustomerSelectorProps> = ({
  appointmentType
}) => {
  const { t } = useTranslation('manage');
  const effectiveClientId = useEffectiveClientId();

  const customersQuery = useClientCustomersQuery(
    effectiveClientId ?? '',
    appointmentType === 'user' && !!effectiveClientId
  );

  const options = useMemo(
    () =>
      (customersQuery.data?.users ?? []).map((customer) => ({
        value: customer.id,
        label: formatCustomerDisplayName(customer)
      })),
    [customersQuery.data?.users]
  );

  if (appointmentType !== 'user') {
    return null;
  }

  return (
    <Form.Item
      label={t('appointmentComponents.create.customer.label')}
      name="userId"
      rules={[{ required: true, message: t('appointmentComponents.create.customer.required') }]}
    >
      <Select
        size="middle"
        showSearch
        allowClear
        optionFilterProp="label"
        placeholder={t('appointmentComponents.create.customer.placeholder')}
        loading={customersQuery.isLoading}
        options={options}
        notFoundContent={
          customersQuery.isLoading ? null : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('appointmentComponents.create.customer.empty')}
            />
          )
        }
      />
    </Form.Item>
  );
};
